/* eslint-disable max-len */
import { provide, inject, ref, reactive, } from 'vue';
import StyleManager from './StyleManager';
import TrackFilterControls from './TrackFilterControls';
import GroupFilterControls from './GroupFilterControls';
import CameraStore from './CameraStore';
import ConfigurationManager from './ConfigurationManager';
/**
 * Type definitions are read only because injectors may mutate internal state,
 * but should never overwrite or delete the injected object.
 */
const AnnotatorPreferencesSymbol = Symbol('annotatorPreferences');
const AttributesSymbol = Symbol('attributes');
const AttributesFilterSymbol = Symbol('attributesFilter');
const TimelineFiltersSymbol = Symbol('timelineFilters');
const DatasetIdSymbol = Symbol('datasetID');
const EditingModeSymbol = Symbol('editingMode');
const MultiSelectSymbol = Symbol('multiSelect');
const PendingSaveCountSymbol = Symbol('pendingSaveCount');
const ProgressSymbol = Symbol('progress');
const RevisionIdSymbol = Symbol('revisionId');
const LatestRevisionIdSymbol = Symbol('latestRevisionId');
const SelectedCameraSymbol = Symbol('selectedCamera');
const SelectedKeySymbol = Symbol('selectedKey');
const SelectedTrackIdSymbol = Symbol('selectedTrackId');
const EditingGroupIdSymbol = Symbol('editingGroupId');
const TimeSymbol = Symbol('time');
const VisibleModesSymbol = Symbol('visibleModes');
const ReadOnlyModeSymbol = Symbol('readOnlyMode');
const ImageEnhancementsSymbol = Symbol('imageEnhancements');
/** Class-based symbols */
const CameraStoreSymbol = Symbol('cameraStore');
const ConfigurationManagerSymbol = Symbol('configurationManager');
const UINotificationSymbol = Symbol('uiNotification');
const TrackStyleManagerSymbol = Symbol('trackTypeStyling');
const GroupStyleManagerSymbol = Symbol('groupTypeStyling');
const TrackFilterControlsSymbol = Symbol('trackFilters');
const GroupFilterControlsSymbol = Symbol('groupFilters');
const HandlerSymbol = Symbol('handler');
/**
 * Make a trivial noop handler. Useful if you only intend to
 * override some small number of values.
 * @param handle callbacl for all handler methods
 */
function dummyHandler(handle) {
    return {
        save(...args) { handle('save', args); return Promise.resolve(); },
        trackSeek(...args) { handle('trackSeek', args); },
        trackEdit(...args) { handle('trackEdit', args); },
        trackSelect(...args) { handle('trackSelect', args); },
        trackSelectNext(...args) { handle('trackSelectNext', args); },
        trackSplit(...args) { handle('trackSplit', args); },
        trackAdd(...args) { handle('trackAdd', args); return 0; },
        updateRectBounds(...args) { handle('updateRectBounds', args); },
        updateGeoJSON(...args) { handle('updateGeoJSON', args); },
        removeTrack(...args) { handle('removeTrack', args); },
        removeGroup(...args) { handle('removeGroup', args); },
        removePoint(...args) { handle('removePoint', args); },
        removeAnnotation(...args) { handle('removeAnnotation', args); },
        selectCamera(...args) { handle('selectCamera', args); },
        selectFeatureHandle(...args) { handle('selectFeatureHandle', args); },
        setAttribute(...args) { handle('setAttribute', args); },
        deleteAttribute(...args) { handle('deleteAttribute', args); },
        toggleMerge(...args) { handle('toggleMerge', args); return []; },
        commitMerge(...args) { handle('commitMerge', args); },
        groupAdd(...args) { handle('groupAdd', args); },
        groupEdit(...args) { handle('groupEdit', args); },
        unstageFromMerge(...args) { handle('unstageFromMerge', args); },
        reloadAnnotations(...args) { handle('reloadTracks', args); return Promise.resolve(); },
        setSVGFilters(...args) { handle('setSVGFilter', args); },
        unlinkCameraTrack(...args) { handle('unlinkCameraTrack', args); },
        linkCameraTrack(...args) { handle('linkCameraTrack', args); },
        startLinking(...args) { handle('startLinking', args); },
        stopLinking(...args) { handle('stopLinking', args); },
        addFullFrameTrack(...args) { handle('addFullFrameTrack', args); },
        processAction(...args) { handle('processAction', args); },
        seekFrame(...args) { handle('seekFrame', args); },
        toggleKeyFrame(...args) { handle('toggleKeyFrame', args); },
    };
}
// eslint-disable-next-line @typescript-eslint/no-empty-function
const markChangesPending = () => { };
// eslint-disable-next-line @typescript-eslint/no-empty-function
const saveConfig = async () => { };
/**
 * make a trivial state store. Useful if you only
 * intend to override some small number of values.
 */
function dummyState() {
    const cameraStore = new CameraStore({ markChangesPending });
    const configurationManager = new ConfigurationManager({
        configurationId: ref(';'), isConfigurationQueryParam: ref(false), setConfigurationId: markChangesPending, saveConfiguration: saveConfig, transferConfiguration: markChangesPending,
    });
    const groupFilterControls = new GroupFilterControls({
        sorted: cameraStore.sortedGroups,
        remove: cameraStore.removeGroups,
        markChangesPending,
    });
    const trackFilterControls = new TrackFilterControls({
        sorted: cameraStore.sortedTracks,
        remove: cameraStore.removeTracks,
        markChangesPending,
        groupFilterControls,
        lookupGroups: cameraStore.lookupGroups,
    });
    return {
        annotatorPreferences: ref({
            trackTails: { before: 20, after: 10 },
            overlays: [{
                    enabled: true, opacity: 25, colorTransparency: false, colorScale: false,
                }],
        }),
        attributes: ref([]),
        cameraStore,
        configurationManager,
        uiNotification: { diveActionShortcuts: ref([]) },
        datasetId: ref(''),
        editingMode: ref(false),
        multiSelectList: ref([]),
        pendingSaveCount: ref(0),
        progress: reactive({ loaded: true }),
        revisionId: ref(0),
        latestRevisionId: ref(0),
        groupFilters: groupFilterControls,
        groupStyleManager: new StyleManager({ markChangesPending }),
        selectedCamera: ref('singleCam'),
        selectedKey: ref(''),
        selectedTrackId: ref(null),
        editingGroupId: ref(null),
        time: {
            frame: ref(0),
            flick: ref(0),
            frameRate: ref(0),
            originalFps: ref(null),
        },
        trackFilters: trackFilterControls,
        trackStyleManager: new StyleManager({ markChangesPending }),
        visibleModes: ref(['rectangle', 'text']),
        readOnlyMode: ref(false),
        imageEnhancements: ref({}),
    };
}
/**
 * Provide global state and handler for a single instance
 * of vue-media-annotator.  Multiple annotator windows
 * are currently not supported.
 *
 * @param {State} state
 * @param {Handler} handler
 * @param {}
 */
function provideAnnotator(state, handler, attributesFilters, timelineFilters) {
    provide(AnnotatorPreferencesSymbol, state.annotatorPreferences);
    provide(AttributesSymbol, state.attributes);
    provide(CameraStoreSymbol, state.cameraStore);
    provide(ConfigurationManagerSymbol, state.configurationManager);
    provide(UINotificationSymbol, state.uiNotification);
    provide(DatasetIdSymbol, state.datasetId);
    provide(EditingModeSymbol, state.editingMode);
    provide(GroupFilterControlsSymbol, state.groupFilters);
    provide(GroupStyleManagerSymbol, state.groupStyleManager);
    provide(MultiSelectSymbol, state.multiSelectList);
    provide(PendingSaveCountSymbol, state.pendingSaveCount);
    provide(ProgressSymbol, state.progress);
    provide(RevisionIdSymbol, state.revisionId);
    provide(LatestRevisionIdSymbol, state.latestRevisionId);
    provide(TrackFilterControlsSymbol, state.trackFilters);
    provide(TrackStyleManagerSymbol, state.trackStyleManager);
    provide(SelectedCameraSymbol, state.selectedCamera);
    provide(SelectedKeySymbol, state.selectedKey);
    provide(SelectedTrackIdSymbol, state.selectedTrackId);
    provide(EditingGroupIdSymbol, state.editingGroupId);
    provide(TimeSymbol, state.time);
    provide(VisibleModesSymbol, state.visibleModes);
    provide(ReadOnlyModeSymbol, state.readOnlyMode);
    provide(ImageEnhancementsSymbol, state.imageEnhancements);
    provide(HandlerSymbol, handler);
    provide(AttributesFilterSymbol, attributesFilters);
    provide(TimelineFiltersSymbol, timelineFilters);
}
function _handleMissing(s) {
    return new Error(`Missing provided object for symbol ${s.toString()}: must provideAnnotator()`);
}
function use(s) {
    const v = inject(s);
    if (v === undefined) {
        throw _handleMissing(s);
    }
    return v;
}
function useAnnotatorPreferences() {
    return use(AnnotatorPreferencesSymbol);
}
function useAttributes() {
    return use(AttributesSymbol);
}
function useAttributesFilters() {
    return use(AttributesFilterSymbol);
}
function useTimelineFilters() {
    return use(TimelineFiltersSymbol);
}
function useCameraStore() {
    return use(CameraStoreSymbol);
}
function useConfiguration() {
    return use(ConfigurationManagerSymbol);
}
function useUINotifications() {
    return use(UINotificationSymbol);
}
function useDatasetId() {
    return use(DatasetIdSymbol);
}
function useEditingMode() {
    return use(EditingModeSymbol);
}
function useGroupFilterControls() {
    return use(GroupFilterControlsSymbol);
}
function useGroupStyleManager() {
    return use(GroupStyleManagerSymbol);
}
function useHandler() {
    return use(HandlerSymbol);
}
function useMultiSelectList() {
    return use(MultiSelectSymbol);
}
function usePendingSaveCount() {
    return use(PendingSaveCountSymbol);
}
function useProgress() {
    return use(ProgressSymbol);
}
function useRevisionId() {
    return use(RevisionIdSymbol);
}
function useLatestRevisionId() {
    return use(LatestRevisionIdSymbol);
}
function useTrackStyleManager() {
    return use(TrackStyleManagerSymbol);
}
function useSelectedCamera() {
    return use(SelectedCameraSymbol);
}
function useSelectedKey() {
    return use(SelectedKeySymbol);
}
function useSelectedTrackId() {
    return use(SelectedTrackIdSymbol);
}
function useEditingGroupId() {
    return use(EditingGroupIdSymbol);
}
function useTime() {
    return use(TimeSymbol);
}
function useTrackFilters() {
    return use(TrackFilterControlsSymbol);
}
function useVisibleModes() {
    return use(VisibleModesSymbol);
}
function useReadOnlyMode() {
    return use(ReadOnlyModeSymbol);
}
function useImageEnhancements() {
    return use(ImageEnhancementsSymbol);
}
export { dummyHandler, dummyState, provideAnnotator, use, useAnnotatorPreferences, useAttributes, useCameraStore, useConfiguration, useUINotifications, useDatasetId, useEditingMode, useHandler, useGroupFilterControls, useGroupStyleManager, useMultiSelectList, usePendingSaveCount, useProgress, useRevisionId, useLatestRevisionId, useTrackFilters, useTrackStyleManager, useSelectedCamera, useSelectedKey, useSelectedTrackId, useEditingGroupId, useTime, useVisibleModes, useReadOnlyMode, useImageEnhancements, useAttributesFilters, useTimelineFilters, };
